import { mdiDelete, mdiImageBrokenVariant, mdiVideo } from "@mdi/js";
import Icon from "@mdi/react";
import moment from "moment";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import { AttachmentModel } from "../../../../../service/models/attachment_modal";
import {
  AttachComplaintAttachmentApi,
  DeleteComplaintAttachmentRepo,
  GetComplaintAttachmentRepo,
  GetComplaintAttachmentSignedUrlApi,
  UploadComplaintAttachmentToS3Api,
} from "../../../../../service/repos/support/complaint_repo";
import {
  attachement_belong,
  COMPLAINT_ATTACHMENT_LIMITS,
  FileType,
} from "../../../../../utils/enums";
import {
  checkCompressedSize,
  formatSizeMB,
} from "../../../../../utils/attachment_validator";
import {
  compressVideo,
  CompressionStage,
  shouldCompressVideo,
} from "../../../../../utils/video_compressor";
import PdfTile from "../../../../components/common/pdf_tile";
import LoadingWidget from "../../../../components/loading_spinner";
import FileUploadWidget from "../../../../new_components/common/file_upload";
import ModalDialog from "../../../../new_components/common/modal";
import StadiumButton from "../../../../new_components/common/stadium_button";

const TYPE_LABEL: Record<string, string> = {
  [FileType.IMAGE]: "Image",
  [FileType.VIDEO]: "Video",
  [FileType.PDF]: "Document",
  [FileType.CSV]: "Document",
  [FileType.UNKOWN]: "File",
};

interface AttachmentWidgetProps {
  complaint_id: string | undefined;
}

const AttachmentWidget: FunctionComponent<AttachmentWidgetProps> = ({
  complaint_id,
}) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState<AttachmentModel[]>([]);
  const [showAddAttachment, setShoAddAttachment] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<AttachmentModel>();
  const [videoCompression, setVideoCompression] = useState<{
    name: string;
    stage: CompressionStage;
    percent: number;
    cancel: () => void;
  }>();
  const [videoUploadPercent, setVideoUploadPercent] = useState<{
    name: string;
    percent: number;
  }>();
  const videoInputRef = useRef<HTMLInputElement>(null);

  const videoCount = attachments.filter(
    (a) => a.type === FileType.VIDEO,
  ).length;

  const getAttachments = async () => {
    setLoading(true);

    const res = await GetComplaintAttachmentRepo(complaint_id!);
    if (res.success) {
      setAttachments(res.data);
    } else {
      showToast({ type: ToastType.error, text: res.error ?? "" });
    }
    setLoading(false);
  };

  const deleteAttachment = async (file: AttachmentModel) => {
    if (!file.id) return;
    if (
      !window.confirm(
        `Delete this ${(TYPE_LABEL[file.type] ?? "file").toLowerCase()}${
          file.name ? ` (${file.name})` : ""
        }? This cannot be undone.`,
      )
    )
      return;

    setLoading(true);
    const res = await DeleteComplaintAttachmentRepo(complaint_id!, file.id);
    if (res.success) {
      getAttachments();
      showToast({
        type: ToastType.success,
        text: res.message || "Attachment deleted successfully",
      });
    } else {
      showToast({ type: ToastType.error, text: res.error ?? "" });
      setLoading(false);
    }
  };

  /**
   * Compresses, then PUTs straight to S3 with a presigned url — only once that
   * PUT succeeds is the object linked to the complaint, so a failure never
   * leaves a dangling reference.
   */
  const uploadVideo = async (file: File) => {
    const videoLimit = COMPLAINT_ATTACHMENT_LIMITS.video;
    if (videoLimit?.max_count && videoCount >= videoLimit.max_count) {
      showToast({
        type: ToastType.error,
        text: `Max ${videoLimit.max_count} videos per complaint. Remove one before adding another.`,
      });
      return;
    }

    let prepared = file;
    if (shouldCompressVideo(file)) {
      const controller = new AbortController();
      setVideoCompression({
        name: file.name,
        stage: "loading",
        percent: 0,
        cancel: () => controller.abort(),
      });

      try {
        const result = await compressVideo(file, {
          signal: controller.signal,
          onProgress: ({ stage, percent }) =>
            setVideoCompression((prev) =>
              prev ? { ...prev, stage, percent: Math.round(percent) } : prev,
            ),
        });

        const tooLarge = checkCompressedSize(result.file);
        if (tooLarge) {
          showToast({
            type: ToastType.error,
            text: result.compressed
              ? tooLarge
              : `Could not compress "${file.name}" (${
                  result.fallbackReason ?? "compression failed"
                }). ${tooLarge}`,
          });
          return;
        }

        if (result.compressed) {
          showToast({
            type: ToastType.success,
            text: `"${file.name}" compressed from ${formatSizeMB(
              result.originalSize,
            )} to ${formatSizeMB(result.file.size)}.`,
          });
        }

        prepared = result.file;
      } finally {
        setVideoCompression(undefined);
      }
    }

    setVideoUploadPercent({ name: prepared.name, percent: 0 });

    try {
      const extension = prepared.name.split(".").pop() ?? "mp4";
      const signed = await GetComplaintAttachmentSignedUrlApi({
        mimetype: prepared.type,
        extension,
      });
      if (!signed.success) {
        showToast({ type: ToastType.error, text: signed.error ?? "" });
        return;
      }

      const { signed_url, url } = signed.data;
      const uploaded = await UploadComplaintAttachmentToS3Api({
        signed_url,
        file: prepared,
        onProgress: (percent) =>
          setVideoUploadPercent({ name: prepared.name, percent }),
      });
      if (!uploaded.success) {
        showToast({
          type: ToastType.error,
          text: `Could not upload "${prepared.name}". ${
            uploaded.error ?? "Please try again."
          }`,
        });
        return;
      }

      const attached = await AttachComplaintAttachmentApi(complaint_id!, {
        url,
        mimetype: prepared.type,
        name: prepared.name,
      });
      if (!attached.success) {
        showToast({ type: ToastType.error, text: attached.error ?? "" });
        return;
      }

      setShoAddAttachment(false);
      getAttachments();
      showToast({
        type: ToastType.success,
        text: `"${prepared.name}" uploaded successfully`,
      });
    } finally {
      setVideoUploadPercent(undefined);
    }
  };

  useEffect(() => {
    getAttachments();
  }, []);

  return (
    <>
      {selectedFile && (
        <ModalDialog
          id="complaint_attachments_preview_modal"
          title={TYPE_LABEL[selectedFile.type] ?? "File"}
          show={selectedFile !== undefined}
          onClose={() => setSelectedFile(undefined)}
        >
          {" "}
          <div className="max-h-96">
            {selectedFile.type === FileType.VIDEO ? (
              <video
                className={`rounded-lg w-96 h-auto `}
                id="cmsVideo"
                autoPlay
                muted
                playsInline
                controls
              >
                <source src={selectedFile.url} type="video/mp4" />
              </video>
            ) : selectedFile.type === FileType.IMAGE ? (
              <img src={selectedFile.url} className="w-96 h-auto"></img>
            ) : selectedFile.type === FileType.PDF ? (
              <>
                <embed
                  src={selectedFile.url}
                  type="application/pdf"
                  className="w-96 h-auto"
                ></embed>{" "}
              </>
            ) : (
              <>Unkown format</>
            )}
          </div>
        </ModalDialog>
      )}
      {showAddAttachment && (
        <ModalDialog
          id="complaint_attachments_upload_modal"
          title="Upload attachment"
          show={showAddAttachment}
          onClose={() => setShoAddAttachment(false)}
        >
          <div className="p-5 space-y-5">
            <div>
              <p className="text-sm font-semibold mb-2">Photo / document</p>
              <FileUploadWidget
                onComplete={() => {
                  getAttachments();
                  setShoAddAttachment(false);
                }}
                belong={attachement_belong.complaint}
                ref_id={complaint_id!}
                limits={{
                  image: COMPLAINT_ATTACHMENT_LIMITS.image,
                  doc: COMPLAINT_ATTACHMENT_LIMITS.doc,
                }}
                accept="image/*,application/pdf"
                transport="backend"
              />
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-semibold mb-2">Video</p>
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                disabled={!!videoCompression || !!videoUploadPercent}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (videoInputRef.current) videoInputRef.current.value = "";
                  if (file) uploadVideo(file);
                }}
              />
              <p className="text-xs text-gray-400 mt-1">
                {COMPLAINT_ATTACHMENT_LIMITS.video?.max_input_size_mb} MB max,
                compressed to under{" "}
                {COMPLAINT_ATTACHMENT_LIMITS.video?.max_size_mb} MB · max{" "}
                {COMPLAINT_ATTACHMENT_LIMITS.video?.max_count} per complaint
              </p>

              {videoCompression && (
                <div className="my-3 border rounded p-3 bg-gray-50">
                  <div className="flex justify-between text-xs font-semibold">
                    <span className="truncate max-w-xs">
                      {videoCompression.stage === "loading"
                        ? "Preparing video compressor (one-time 31 MB download)…"
                        : `Compressing ${videoCompression.name}…`}
                    </span>
                    <span>{videoCompression.percent}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 my-2">
                    <div
                      className="bg-green-600 h-1.5 rounded-full"
                      style={{
                        width: `${
                          videoCompression.percent < 2
                            ? 2
                            : videoCompression.percent
                        }%`,
                      }}
                    />
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="text-xs border rounded-full px-2 py-1"
                      onClick={videoCompression.cancel}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {videoUploadPercent && (
                <div className="my-3">
                  <div className="flex justify-between text-xs">
                    <span className="truncate max-w-xs">
                      Uploading {videoUploadPercent.name}…
                    </span>
                    <span>{videoUploadPercent.percent}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-blue-600 h-1.5 rounded-full"
                      style={{
                        width: `${
                          videoUploadPercent.percent < 2
                            ? 2
                            : videoUploadPercent.percent
                        }%`,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </ModalDialog>
      )}
      <div
        className=" w-full  p-5"
        style={{ height: "80vh", minWidth: "50vw" }}
      >
        <div className="">
          <div className="flex justify-end my-2">
            <StadiumButton
              id="complaint_attachments_upload_btn"
              onTap={() => setShoAddAttachment(true)}
            >
              {" "}
              Upload Attachment
            </StadiumButton>
          </div>{" "}
          <div className="flex flex-col gap-2    md:hidden">
            {attachments.map((file, i) => (
              <div
                id={`complaint_attachments_row_mobile_${file.id ?? i}`}
                className=" flex  gap-2 justify-center border rounded items-center  h-auto cursor-pointer"
                key={i}
              >
                <div className="w-28">
                  <div
                    id={`complaint_attachments_row_mobile_${file.id ?? i}_preview_btn`}
                    className="flex justify-center items-center   w-28 h-20  bg-gray-200"
                    onClick={() => {
                      console.log(file);

                      setSelectedFile(file);
                    }}
                  >
                    {file.type === FileType.IMAGE ? (
                      <img src={file.url} alt="" className="h-20 w-28  " />
                    ) : file.type === FileType.PDF ? (
                      <PdfTile url={file.url} />
                    ) : file.type === FileType.VIDEO ? (
                      <Icon path={mdiVideo} size="30" />
                    ) : (
                      <Icon path={mdiImageBrokenVariant} size="30" />
                    )}
                  </div>
                </div>
                <div className="text-xs w-28">
                  <p className="font-semibold">
                    {TYPE_LABEL[file.type] ?? "File"}
                  </p>
                  <p className="truncate" title={file.name}>
                    {file.name ?? "--"}
                  </p>
                  <p>{file.created_by_name}</p>
                  <p> {moment(file.created_at).format("DD MMM YYYY")}</p>
                </div>
                <button
                  type="button"
                  className="text-red-500 hover:text-red-700 px-2"
                  title="Delete"
                  onClick={() => deleteAttachment(file)}
                >
                  <Icon path={mdiDelete} size="20" />
                </button>
              </div>
            ))}
          </div>
          <div className="  hidden md:block">
            <table className=" w-full text-xs mb-5 border rounded-sm ">
              <thead>
                <tr className="border-b">
                  <th></th>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Created Date</th>
                  <th>Created by</th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {attachments.map((file, i) => (
                  <tr id={`complaint_attachments_row_${file.id ?? i}`} key={i}>
                    <td align="center">
                      <div
                        id={`complaint_attachments_row_${file.id ?? i}_preview_btn`}
                        className="flex justify-center items-center rounded-full h-14 w-14 overflow-hidden bg-gray-200 cursor-pointer"
                        onClick={() => {
                          console.log(file);

                          setSelectedFile(file);
                        }}
                      >
                        {file.type === FileType.IMAGE ? (
                          <img
                            src={file.url}
                            alt=""
                            className="h-14 w-14 rounded-full"
                          />
                        ) : file.type === FileType.PDF ? (
                          <PdfTile url={file.url} />
                        ) : file.type === FileType.VIDEO ? (
                          <Icon path={mdiVideo} size="30" />
                        ) : (
                          <Icon path={mdiImageBrokenVariant} size="30" />
                        )}
                      </div>
                    </td>
                    <td align="center">{TYPE_LABEL[file.type] ?? "File"}</td>
                    <td
                      align="center"
                      className="max-w-[10rem] truncate"
                      title={file.name}
                    >
                      {file.name ?? "--"}
                    </td>
                    <td align="center">
                      {moment(file.created_at).format("DD MMM YYYY")}
                    </td>
                    <td align="center">{file.created_by_name}</td>{" "}
                    <td>
                      <div className="">
                        <a
                          id={`complaint_attachments_row_${file.id ?? i}_view_link`}
                          target={"_blank"}
                          rel="noreferrer"
                          href={file.url}
                        >
                          View
                        </a>
                      </div>
                    </td>
                    <td align="center">
                      <button
                        type="button"
                        className="text-red-500 hover:text-red-700"
                        title="Delete"
                        onClick={() => deleteAttachment(file)}
                      >
                        <Icon path={mdiDelete} size="18" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <LoadingWidget loading={loading} />
    </>
  );
};

export default AttachmentWidget;
